import { NocfoPlan } from '@constants'
import { network } from '@utils'
import { handleReturn, QueryFunction, MutationFunction, ActionFunction } from './root'

interface BillingProps {
  slug: string
}

export interface IPlanPrice {
  id: string
  interval: string
  amount: number
  currency: string
}

export interface IPlanOption {
  id: NocfoPlan | string
  name: string
  plan: 'FREE' | 'PLUS' | 'PRO'
  prices: IPlanPrice[]
}

export const fetchSubscriptionPrices: QueryFunction<BillingProps, IPlanOption[]> = async ({
  slug
}) => {
  const url = `/v1/billing/${slug}/stripe/prices/`
  const { data } = await network.httpClient.request({
    url,
    method: 'GET'
  })
  return data
}

export interface ICheckoutSessionProps {
  sessionId: string
}

export type LucaCreditBundleCheckoutResult = {
  flow: 'luca_credit_bundle'
  slug: string
  available_luca_credits: number
  transaction_id?: string
}

export type PlanUpgradeCheckoutResult = {
  business_id: string
  slug?: string
  value?: number
  currency?: string
  transaction_id?: string
}

export type ICheckoutSessionResultProps = LucaCreditBundleCheckoutResult | PlanUpgradeCheckoutResult

export const isLucaCreditBundleCheckout = (
  result: ICheckoutSessionResultProps
): result is LucaCreditBundleCheckoutResult =>
  'flow' in result && result.flow === 'luca_credit_bundle'

export const completeCheckoutSession: QueryFunction<
  ICheckoutSessionProps,
  ICheckoutSessionResultProps
> = async ({ sessionId }) => {
  const url = `/v1/billing/stripe/checkout/complete/${sessionId}/`
  const { data } = await network.httpClient.request({
    url,
    method: 'GET'
  })
  return data
}

export interface ICompletePaymentMethodSetupSessionProps {
  sessionId: string
}

export interface ICompletePaymentMethodSetupSessionResult {
  slug: string
  return_url: string
  payment_method_id: string
  set_as_default: boolean
}

export const completePaymentMethodSetupSession: QueryFunction<
  ICompletePaymentMethodSetupSessionProps,
  ICompletePaymentMethodSetupSessionResult
> = async ({ sessionId }) => {
  const url = `/v1/billing/stripe/payment-methods/setup/complete/${sessionId}/`
  const { data } = await network.httpClient.request({
    url,
    method: 'GET'
  })
  return data
}

export interface IChurnReasonOption {
  value: string
  label: string
  allows_custom_text: boolean
}

export const fetchChurnReasons: QueryFunction<BillingProps, IChurnReasonOption[]> = async ({
  slug
}) => {
  const url = `/v1/billing/${slug}/stripe/subscription/churn-reasons/`
  const { data } = await network.httpClient.request({
    url,
    method: 'GET'
  })
  return data.reasons
}

export interface CancelSubscriptionProps {
  slug: string
  disableEinvoicing: boolean
  churnReasons?: string[]
  churnReasonOther?: string
  keepAddons?: boolean
}

export const cancelSubscription: MutationFunction<CancelSubscriptionProps, void> = async ({
  slug,
  disableEinvoicing,
  churnReasons,
  churnReasonOther,
  keepAddons
}) => {
  const url = `/v1/billing/${slug}/stripe/subscription/cancel/`
  const payload: Record<string, unknown> = {
    disable_einvoicing: disableEinvoicing
  }

  if (churnReasons?.length) {
    payload.churn_reasons = churnReasons
    payload.churn_reason_other = churnReasonOther
  }

  if (keepAddons !== undefined) {
    payload.keep_addons = keepAddons
  }

  const { status, data } = await network.httpClient.request({
    url,
    method: 'POST',
    data: payload,
    validateStatus: () => true
  })
  return handleReturn({ status, data })
}

export const endTrialSubscription: MutationFunction<BillingProps, void> = async ({ slug }) => {
  const url = `/v1/billing/${slug}/stripe/subscription/end-trial/`
  await network.httpClient.request({
    url,
    method: 'POST',
    data: null
  })
}

export interface IReceipt {
  id: string
  created: string
  receipt_url: string
  amount: number
  currency: string
}

export const fetchReceipts: QueryFunction<BillingProps, IReceipt[]> = async ({ slug }) => {
  const url = `/v1/billing/${slug}/stripe/receipts/`
  const { data } = await network.httpClient.request({
    url,
    method: 'GET'
  })
  return data
}

export interface ISubscriptionInfo {
  plan: string
  interval: string
  amount: number
  currency: string
}

export const fetchSubscriptionInfo: QueryFunction<BillingProps, ISubscriptionInfo> = async ({
  slug
}) => {
  const url = `/v1/billing/${slug}/stripe/subscription/info/`
  const { data } = await network.httpClient.request({
    url,
    method: 'GET'
  })
  return data
}

export interface IPaygUsageItem {
  price_id: string
  product_key: string | null
  product_name: string
  quantity: number
  unit_amount: number | null
  amount: number
  currency: string | null
  period_start: string | null
  period_end: string | null
  is_estimate?: boolean
}

export interface IPaygUsageSummary {
  currency: string | null
  subtotal: number
  items: IPaygUsageItem[]
  period_start: string | null
  period_end: string | null
}

export const fetchPaygUsage: QueryFunction<BillingProps, IPaygUsageSummary> = async ({ slug }) => {
  const url = `/v1/billing/${slug}/stripe/subscription/payg-usage/`
  const { data } = await network.httpClient.request({
    url,
    method: 'GET'
  })
  return data
}

export interface ChangeSubscriptionProps {
  slug: string
}

export interface ISubscriptionChange {
  product: string
  price: string
}

export const changeSubscription: ActionFunction<
  ChangeSubscriptionProps,
  ISubscriptionChange,
  void
> = async ({ slug }, data) => {
  const url = `/v1/billing/${slug}/stripe/subscription/change/`
  await network.httpClient.request({
    url,
    method: 'POST',
    data
  })
}

// --- Custom pricing table ---

export interface IPricingPlan {
  id: string
  name: string
  contact_sales: boolean
  monthly_price_id: string | null
  monthly_price_eur: number | null
  yearly_price_id: string | null
  yearly_price_eur: number | null
  monthly_documents: number | null // null means unlimited
  unlimited_seats: boolean
  description: string | null
}

export interface IPricingTableData {
  trial_available: boolean
  plans: IPricingPlan[]
}

export const fetchPricingPlans: QueryFunction<BillingProps, IPricingTableData> = async ({
  slug
}) => {
  const url = `/v1/billing/${slug}/stripe/plans/`
  const { data } = await network.httpClient.request({
    url,
    method: 'GET'
  })
  return data
}

export interface ICreateCheckoutSessionProps {
  slug: string
}

export interface ICreateCheckoutSessionPayload {
  price_id: string
  promotion_code?: string
  addons?: Array<{
    addon_id: string
  }>
}

export interface ICreateCheckoutSessionResult {
  checkout_url?: string | null
  completed?: boolean
}

export const createCheckoutSession: ActionFunction<
  ICreateCheckoutSessionProps,
  ICreateCheckoutSessionPayload,
  ICreateCheckoutSessionResult
> = async ({ slug }, data) => {
  const url = `/v1/billing/${slug}/stripe/checkout/`
  const { status, data: result } = await network.httpClient.request({
    url,
    method: 'POST',
    data,
    validateStatus: () => true
  })
  return handleReturn({ status, data: result })
}

export interface IPaymentMethodStatus {
  has_payment_method: boolean
  has_manual_invoicing: boolean
  usage_billing_payment_at_risk: boolean
}

export const fetchPaymentMethodStatus: QueryFunction<BillingProps, IPaymentMethodStatus> = async ({
  slug
}) => {
  const url = `/v1/billing/${slug}/stripe/payment-method/`
  const { data } = await network.httpClient.request({
    url,
    method: 'GET'
  })
  return data
}

export interface IPaymentMethod {
  id: string
  type: string
  is_default: boolean
  brand?: string | null
  last4?: string | null
  exp_month?: number | null
  exp_year?: number | null
}

export interface IPaymentMethodsResult {
  payment_methods: IPaymentMethod[]
}

export interface ILucaCreditBundle {
  id: string
  credits: number
  amount: number
  list_amount: number
  currency: string
}

export interface ILucaCreditBundleQuote {
  id: string
  credits: number
  amount: number
  currency: string
  tax_amount: number | null
  tax_rate_percent: number | null
  total_amount: number | null
}

export interface ILucaCreditBundlePurchaseResult {
  completed: boolean
  checkout_url?: string
  available_luca_credits?: number
}

export const fetchLucaCreditBundles: QueryFunction<BillingProps, ILucaCreditBundle[]> = async ({
  slug
}) => {
  const url = `/v1/billing/${slug}/stripe/luca-credit-bundles/`
  const { data } = await network.httpClient.request<{ bundles: ILucaCreditBundle[] }>({
    url,
    method: 'GET'
  })
  return data.bundles
}

export const fetchLucaCreditBundleQuote: QueryFunction<
  BillingProps & { bundleId: string },
  ILucaCreditBundleQuote
> = async ({ slug, bundleId }) => {
  const url = `/v1/billing/${slug}/stripe/luca-credit-bundles/${bundleId}/preview/`
  const { data } = await network.httpClient.request<ILucaCreditBundleQuote>({
    url,
    method: 'GET'
  })
  return data
}

export const purchaseLucaCreditBundle: ActionFunction<
  BillingProps,
  { bundle_id: string; idempotency_key: string },
  ILucaCreditBundlePurchaseResult
> = async ({ slug }, data) => {
  const url = `/v1/billing/${slug}/stripe/luca-credit-bundles/`
  const { data: result } = await network.httpClient.request<ILucaCreditBundlePurchaseResult>({
    url,
    method: 'POST',
    data
  })
  return result
}

export const setLucaPaygConsent: ActionFunction<
  BillingProps,
  { luca_payg_allowed: boolean },
  { luca_payg_allowed: boolean }
> = async ({ slug }, data) => {
  const url = `/v1/billing/${slug}/stripe/luca-payg-consent/`
  const { data: result } = await network.httpClient.request<{ luca_payg_allowed: boolean }>({
    url,
    method: 'POST',
    data
  })
  return result
}

export const fetchPaymentMethods: QueryFunction<BillingProps, IPaymentMethodsResult> = async ({
  slug
}) => {
  const url = `/v1/billing/${slug}/stripe/payment-methods/`
  const { data } = await network.httpClient.request({
    url,
    method: 'GET'
  })
  return data
}

export interface ICreatePaymentMethodSetupSessionProps {
  slug: string
}

export interface ICreatePaymentMethodSetupSessionPayload {
  success_url: string
  return_url?: string
  cancel_url?: string
}

export interface ICreatePaymentMethodSetupSessionResult {
  checkout_url: string
}

export const createPaymentMethodSetupSession: ActionFunction<
  ICreatePaymentMethodSetupSessionProps,
  ICreatePaymentMethodSetupSessionPayload,
  ICreatePaymentMethodSetupSessionResult
> = async ({ slug }, data) => {
  const url = `/v1/billing/${slug}/stripe/payment-methods/setup/`
  const { data: result } = await network.httpClient.request({
    url,
    method: 'POST',
    data
  })
  return result
}

export interface ISetDefaultPaymentMethodProps {
  slug: string
  paymentMethodId: string
}

export const setDefaultPaymentMethod: ActionFunction<
  ISetDefaultPaymentMethodProps,
  undefined,
  void
> = async ({ slug, paymentMethodId }) => {
  const url = `/v1/billing/${slug}/stripe/payment-methods/${paymentMethodId}/default/`
  await network.httpClient.request({
    url,
    method: 'POST'
  })
}

export interface IDeletePaymentMethodProps {
  slug: string
  paymentMethodId: string
}

export const deletePaymentMethod: ActionFunction<
  IDeletePaymentMethodProps,
  undefined,
  void
> = async ({ slug, paymentMethodId }) => {
  const url = `/v1/billing/${slug}/stripe/payment-methods/${paymentMethodId}/`
  await network.httpClient.request({
    url,
    method: 'DELETE'
  })
}

// --- Stripe add-ons ---

export interface IStripeAddonInfo {
  addon_id: string
  name: string
  description: string
  available: boolean
  active: boolean
  credits_per_unit: number | null
  total_credits: number
  monthly_price_id: string | null
  monthly_price_eur: number | null
}

export interface IStripeAddonsList {
  addons: IStripeAddonInfo[]
}

export const stripeAddonsQueryKey = (slug: string) => [slug, 'stripe', 'addons'] as const

export const fetchStripeAddons: QueryFunction<BillingProps, IStripeAddonsList> = async ({
  slug
}) => {
  const url = `/v1/billing/${slug}/stripe/addons/`
  const { data } = await network.httpClient.request({
    url,
    method: 'GET'
  })
  return data
}

export interface ICreateAddonCheckoutProps {
  slug: string
}

export interface ICreateAddonCheckoutPayload {
  addon_id: string
}

export interface ICreateAddonCheckoutResult {
  checkout_url?: string | null
  completed?: boolean
}

export const createAddonCheckout: ActionFunction<
  ICreateAddonCheckoutProps,
  ICreateAddonCheckoutPayload,
  ICreateAddonCheckoutResult
> = async ({ slug }, data) => {
  const url = `/v1/billing/${slug}/stripe/addons/`
  const { status, data: result } = await network.httpClient.request({
    url,
    method: 'POST',
    data,
    validateStatus: () => true
  })
  return handleReturn({ status, data: result })
}

export interface IRemoveAddonProps {
  slug: string
  addonId: string
}

export const removeAddon: ActionFunction<IRemoveAddonProps, undefined, void> = async ({
  slug,
  addonId
}) => {
  const url = `/v1/billing/${slug}/stripe/addons/${addonId}/`
  await network.httpClient.request({
    url,
    method: 'DELETE'
  })
}

// --- Manual invoicing ---

export type ManualInvoicingMethod = 'EMAIL_INVOICE' | 'E_INVOICE'

export type BillingInterval = 'month' | 'year'

export interface IManualInvoicingRequestProps {
  slug: string
}

export interface IManualInvoicingRequestPayload {
  email: string
  billing_address: string
  postal_code_and_city: string
  plan: NocfoPlan
  billing_interval: BillingInterval
  invoicing_method: ManualInvoicingMethod
  additional_info?: string
}

export const submitManualInvoicingRequest: ActionFunction<
  IManualInvoicingRequestProps,
  IManualInvoicingRequestPayload,
  void
> = async ({ slug }, data) => {
  const url = `/v1/billing/${slug}/manual-invoicing/`
  const { status, data: responseData } = await network.httpClient.request({
    url,
    method: 'POST',
    data,
    validateStatus: () => true
  })
  return handleReturn({ status, data: responseData })
}

export interface ICustomerPortalSession {
  id: string
  url: string
}

export interface IStripeUnpaidInvoice {
  id: string
  number: string | null
  created: string
  due_date: string | null
  amount: number
  currency: string
  status: 'draft' | 'open' | string
  hosted_invoice_url: string | null
}

export interface IStripeUnpaidInvoicesSummary {
  count: number
  total: number
  currency: string | null
  invoices: IStripeUnpaidInvoice[]
}

export const fetchUnpaidInvoices: QueryFunction<
  BillingProps,
  IStripeUnpaidInvoicesSummary
> = async ({ slug }) => {
  const url = `/v1/billing/${slug}/stripe/unpaid-invoices/`
  const { data } = await network.httpClient.request({
    url,
    method: 'GET'
  })
  return data
}

export const fetchCustomerPortalSession: QueryFunction<
  BillingProps,
  ICustomerPortalSession
> = async ({ slug }) => {
  const url = `/v1/billing/${slug}/stripe/customer_portal/`
  const { data } = await network.httpClient.request({
    url,
    method: 'GET'
  })
  return data
}

export interface IBillingDetailsPortalSession {
  id: string
  url: string
}

export const fetchBillingDetailsPortalSession: QueryFunction<
  BillingProps,
  IBillingDetailsPortalSession
> = async ({ slug }) => {
  const url = `/v1/billing/${slug}/stripe/billing-details-portal/`
  const { data } = await network.httpClient.request({
    url,
    method: 'GET'
  })
  return data
}
